/**
 * Offline outbox.
 *
 * Job sites lose signal constantly. Every write the crew makes — clocking in,
 * a progress note with photos, a tool going out — is queued here first and
 * replayed when the network comes back. Each queued item carries a clientId
 * that the API treats as an idempotency key, so a replay that the server
 * already received returns the original record instead of duplicating it.
 */

import { STORE_OUTBOX, idb } from './idb'

export type OutboxKind = 'clock-in' | 'clock-out' | 'switch' | 'note' | 'inventory' | 'geo-event'

export interface OutboxItem {
  id: string
  kind: OutboxKind
  url: string
  method: string
  /** JSON body, or the `payload` field of a multipart request. */
  payload: unknown
  /** Photos, kept as Blobs so they survive a reload. */
  files: { field: string; blob: Blob; filename: string }[]
  /** Human-readable summary for the pending-items UI. */
  summary: string
  createdAt: string
  attempts: number
  lastError: string | null
  nextAttemptAt: string
}

type Listener = (items: OutboxItem[]) => void
const listeners = new Set<Listener>()
let flushing = false

function backoffMs(attempts: number): number {
  // 5s, 15s, 45s, 2m, 5m, then every 5 minutes.
  return Math.min(5 * 60_000, 5000 * 3 ** Math.min(attempts, 4))
}

export function newClientId(): string {
  return crypto.randomUUID()
}

export function subscribeOutbox(listener: Listener): () => void {
  listeners.add(listener)
  void list().then(listener)
  return () => listeners.delete(listener)
}

async function notify(): Promise<void> {
  const items = await list()
  for (const listener of listeners) listener(items)
}

export async function list(): Promise<OutboxItem[]> {
  const items = await idb.getAll<OutboxItem>(STORE_OUTBOX)
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}

export async function enqueue(
  item: Omit<OutboxItem, 'id' | 'createdAt' | 'attempts' | 'lastError' | 'nextAttemptAt'>,
): Promise<OutboxItem> {
  const full: OutboxItem = {
    ...item,
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    attempts: 0,
    lastError: null,
    nextAttemptAt: new Date().toISOString(),
  }
  await idb.put(STORE_OUTBOX, full)
  await notify()
  return full
}

export async function remove(id: string): Promise<void> {
  await idb.delete(STORE_OUTBOX, id)
  await notify()
}

function buildRequest(item: OutboxItem): RequestInit {
  if (item.files.length === 0) {
    return {
      method: item.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item.payload),
      credentials: 'same-origin',
    }
  }
  const form = new FormData()
  form.set('payload', JSON.stringify(item.payload))
  for (const file of item.files) {
    form.set(file.field, new File([file.blob], file.filename, { type: file.blob.type }))
  }
  return { method: item.method, body: form, credentials: 'same-origin' }
}

export interface FlushResult {
  sent: number
  failed: number
  remaining: number
}

/**
 * Try to deliver everything that is due. Safe to call often — items keep an
 * exponential backoff so a server that is down does not get hammered.
 */
export async function flush(): Promise<FlushResult> {
  if (flushing || !navigator.onLine) {
    return { sent: 0, failed: 0, remaining: (await list()).length }
  }
  flushing = true
  let sent = 0
  let failed = 0

  try {
    for (const item of await list()) {
      if (Date.parse(item.nextAttemptAt) > Date.now()) continue

      try {
        const res = await fetch(item.url, buildRequest(item))

        if (res.ok) {
          await idb.delete(STORE_OUTBOX, item.id)
          sent++
          continue
        }

        // 4xx means the request will never succeed as written. Dropping it
        // beats replaying a rejected clock-in forever — except 401/408/429,
        // which are worth another go once the session or rate limit recovers.
        const permanent = res.status >= 400 && res.status < 500 &&
          ![401, 408, 409, 429].includes(res.status)
        // 409 on a clock action means the server already has a newer truth.
        const settled = res.status === 409

        if (permanent || settled) {
          await idb.delete(STORE_OUTBOX, item.id)
          failed++
          continue
        }
        throw new Error(`HTTP ${res.status}`)
      } catch (err) {
        failed++
        await idb.put(STORE_OUTBOX, {
          ...item,
          attempts: item.attempts + 1,
          lastError: err instanceof Error ? err.message : String(err),
          nextAttemptAt: new Date(Date.now() + backoffMs(item.attempts)).toISOString(),
        } satisfies OutboxItem)
      }
    }
  } finally {
    flushing = false
    await notify()
  }

  return { sent, failed, remaining: (await list()).length }
}

let started = false

/** Wire up automatic draining. Call once at app start. */
export function startOutboxDrain(onDrained?: (result: FlushResult) => void): void {
  if (started) return
  started = true

  const run = async () => {
    const result = await flush()
    if (result.sent > 0) onDrained?.(result)
  }

  window.addEventListener('online', () => void run())
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void run()
  })
  setInterval(() => void run(), 30_000)
  void run()
}
